import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import {
  UPLOAD_CONFLICT_RESOLUTION,
  type UploadConflict,
  type UploadConflictResolution,
} from "./types";

interface UploadConflictModalProps {
  conflict: UploadConflict | null;
  onCancel: () => void;
  onResolve: (resolution: UploadConflictResolution) => void;
}

export const UploadConflictModal = ({
  conflict,
  onCancel,
  onResolve,
}: UploadConflictModalProps) => {
  const description = conflict
    ? `You already sent a file called ${conflict.file_name}. Replace it, or keep both?`
    : "";

  return (
    <Dialog
      open={!!conflict}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent description={description}>
        <DialogHeader>
          <DialogTitle>You already sent this file</DialogTitle>
        </DialogHeader>

        {conflict && (
          <Stack className="p-6" gap="md" fullWidth>
            <Typography variant="bodyW1" textColor="secondary">
              You already sent a file called{" "}
              <span className="font-semibold text-primary-text">
                {conflict.file_name}
              </span>
              , on{" "}
              {dayjs(conflict.existing_document.uploaded_at).format(
                "MMM D, YYYY",
              )}
              .
            </Typography>
            <Typography variant="captionW1" textColor="tertiary">
              Replacing removes the old one for good. Keeping both saves this
              one as {conflict.suggested_file_name}.
            </Typography>
          </Stack>
        )}

        <DialogFooter>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => onResolve(UPLOAD_CONFLICT_RESOLUTION.keepBoth)}
          >
            Keep both
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => onResolve(UPLOAD_CONFLICT_RESOLUTION.replace)}
          >
            Replace it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
